<template lang="pug">
control
  field(:class="{ invalid: !fileDrop.meta.valid && fileDrop.meta.touched }")
    label(v-html="fileDrop.label")
    .drop-zone.default(@dragover="fileDrop.dragover", @dragleave="fileDrop.dragleave", @drop="fileDrop.drop")
      input(type="hidden", :value="fileDrop.fileTypes" :id="fileDrop.id + '-hidden'")
      input(:type="fileDrop.type", :id="fileDrop.id", @change="fileDrop.onChange" ref="inputRef" :accept="fileDrop.formats")
      label(:for="fileDrop.id")
        div
          .upload-message
            p Drop file here or click to upload
            i.fa.fa-cloud-upload-alt
          small Supported Files: ({{ fileDrop.formats }})

      .file-list(v-if="fileDrop.model.length > 0")
        .file-list__inner
          div(v-for="(file, index) in fileDrop.model")
            p {{ file.name }}
            i.fa.fa-times.ml-2(type="button", @click="fileDrop.removeFile(index)", title="Remove file")
    error(v-if="!fileDrop.meta.valid && fileDrop.meta.touched") {{ fileDrop.errorMessage }}
</template>

<script>
import { useStore } from 'vuex';
import { reactive, computed, ref } from 'vue';
import { useField } from 'vee-validate';
import * as yup from 'yup';
export default {
  name: 'FileDrop',
  props: {
    field: Object,
  },
  setup(p) {
    const store = useStore();
    const fileDrop = reactive(initFileDrop());

    return { fileDrop };

    function initFileDrop() {

        let labelHtml = p.field.label;
        if (labelHtml && p.field.required !== false ) labelHtml = `<b>${labelHtml}</b>`;

      const id = p.field.id;
      const label = labelHtml ? `${labelHtml}${p.field.required !== false ? " <span class='required'>*</span>" : ""}` : null;
      const extensions = p.field.extensions;
      const formats = p.field.extensions.join(", ");
      const max = p.field.max;
      const type = "file";
      const inputRef = ref(FileList);
      const fileTypes = reactive([]);

      const lookup = [
        { ext: '.doc', mime: 'application/msword' },
        { ext: '.docx', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
        { ext: '.jpg', mime: 'image/jpeg' },
        { ext: '.jpeg', mime: 'image/jpeg' },
        { ext: '.png', mime: 'image/png' },
        { ext: '.pdf', mime: 'application/pdf' },
        { ext: '.xls', mime: 'application/vnd.ms-excel' },
        { ext: '.xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        { ext: '.zip', mime: 'application/zip' },
      ]

      let _regex = [];
      extensions.forEach((ext) => {
        let found = lookup.find( obj => obj['ext'] === ext );
        if (found) {
          _regex.push(found.mime);
        }
      })
      const regex = new RegExp(`(${_regex.join("|")})`, 'gmi');
      const errmsg = ref("Invalid file types")

      let rules = yup.array().min(1).of(yup.string().matches(regex, {message: errmsg.value}))
      if (p.field.required !== false) {
        rules = rules.required();
      }

      const { value, errorMessage, meta, handleBlur, validate } = useField(
        `${id}-hidden`,
        rules,
        {
          validateOnInput: true,
          initialValue: store.getters.model[id],
        }
      );

      const model = computed({
        get: () => store.getters.model[id],
        set: v => {
          store.commit('updateModel', { id, value: v });
        },
      });

      model.value = [];

      const onChange = async (event) => {
        event.preventDefault();
        let _fileList = event.target.files;
        inputRef.value.files = _fileList
        fileTypes.value = Array.from(_fileList).map(file => file.type);
        value.value = fileTypes.value;
        model.value = [...model.value, ...inputRef.value.files];
        await validate();
        handleBlur();
      };
      
      const removeFile = async (i) => {
        model.value.splice(i, 1);
        fileTypes.value = model.value.map(file => file.type);
        value.value = fileTypes.value;
        await validate();
        handleBlur();
      };

      const dragover = (event) => {
        event.preventDefault();
        // Add some visual fluff to show the user can drop its files
        if (!event.currentTarget.classList.contains('hovered')) {
          event.currentTarget.classList.remove('default');
          event.currentTarget.classList.add('hovered');
        }
      };

      const dragleave = (event) => {
        // Clean up
        event.currentTarget.classList.add('default');
        event.currentTarget.classList.remove('hovered');
      };

      const drop = (event) => {
        event.preventDefault();
        // Get all dropped files
        let _fileList = event.dataTransfer.files;

        inputRef.value.files = _fileList
        fileTypes.value = Array.from(_fileList).map(file => file.type);
        value.value = fileTypes.value;

        model.value = [...model.value, ...inputRef.value.files];
        validate();
        handleBlur();

        // Clean up
        event.currentTarget.classList.add('default');
        event.currentTarget.classList.remove('hovered');
      };

      return {
        id,
        label,
        type,
        extensions,
        formats,
        max,
        model,
        meta,
        fileTypes,
        onChange,
        removeFile,
        dragover,
        dragleave,
        drop,
        errorMessage
      };
    }
  },
};
</script>

<style lang="scss" scoped>
  .drop-zone {
    //min-height: 5rem;
    background: #e7e7e7;
    border-radius: 6px;
    box-shadow: 0 0 10px rgba(0,0,0,0.18);
    &.default {
      border: 3px dashed #5d5d5d;
    }
    &.hovered {
      border: 3px dashed #b5121b;
      i {
        opacity: 1;
      }
    }
    input {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      opacity: 0;
    }
    label {
      padding: 1rem;
      display: flex;
      flex-direction: column;
      justify-content: center;
      cursor: pointer;
      div {
        display: block;
        .upload-message {
          display: flex;
          justify-content: center;
          line-height: 36px;
          color: #5d5d5d;
          p {
            font-size: 1.2rem;
            display: inline-block;
            margin: 0;
            padding-right: 1rem;
          }
          i {
            font-size: 1.8rem;
            opacity: .7;
          }
        }
        small {
          text-align: center;
          display: block;
        }
      }
    }
    .file-list {
      padding: 1rem 0;
      height: 100%;
      &__inner div {
        padding: 0 1rem;
        display: flex;
        justify-content: space-between;
        line-height: 20px;
        p {
          margin: 0;
          display: inline-block;
        }
        i {
          cursor: pointer;
          color: #b5121b;
          font-size: 1.4rem;
        }

      }
    }
  }
</style>
